import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import { Component, Inject, Vue, Watch } from "vue-property-decorator";
import {
  CHAT_ACTIONS,
  CHAT_ACTIONS_RESPONSES,
  CHAT_BOT_ROLES,
  CHAT_BOT_TEXT_UTILS,
  CHAT_ERRORS,
  LOGIN_PATH
} from "./Constants/ChatWindow.constants";
import { mapGetters } from "vuex";
import store from "@/store";
import { STORE_CONSTANTS } from "@/shared/constants/GeneralConstants";
import { CHAT_BOT_CONSTANTS } from "@/shared/components/ChatBot/constants/ChatBotConstants";
import { ChatService } from "./Services/ChatWindow.service";
import { Http } from "@status/codes";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { ChatComunication } from "./Model/ChatComunication.model";
import { ChatMessages } from "./Model/ChatMessages.model";

@Component({
  name: "ChatWindow",
  computed: {
    ...mapGetters({
      chatAction: "getChatComunication"
    })
  }
})
export default class ChatWindow extends Vue {
  @Inject() chatService!: ChatService;

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  chatAction!: ChatComunication;
  SpeechRecognition =
    (window as any).SpeechRecognition ||
    (window as any).webkitSpeechRecognition;
  recognition: SpeechRecognition = this.SpeechRecognition
    ? new this.SpeechRecognition()
    : false;
  runtimeTranscription = "";
  error = "";

  textFieldClass = STYLE_CLASSES.TEXT_FIELD;
  CHAT_BOT_TEXTS = CHAT_BOT_TEXT_UTILS;
  CHAT_BOT_ROLES = CHAT_BOT_ROLES;
  ACTIONS = CHAT_ACTIONS;

  inputUser = "";
  messages: ChatMessages[] = [];
  threadId = "";
  assistantId = "";
  showWindow = false;
  disableInput = false;
  socketResponse = false;
  loading = false;

  mounted() {
    this.inputUser = "";
    this.messages = this.chatAction.msgsStore ?? [];

    this.showWindow = this.chatAction.chatOpen ?? false;

    this.threadId = this.chatAction.threadId ?? "";

    this.assistantId = this.chatAction.assistantId ?? "";

    this.$router.beforeEach((to, from, next) => {
      if (to.fullPath != LOGIN_PATH && from.fullPath != LOGIN_PATH) {
        const action: ChatComunication = {
          action: "BackUp",
          chatOpen: this.showWindow,
          msgsStore: this.messages,
          threadId: this.threadId,
          assistantId: this.assistantId
        };
        store.commit(STORE_CONSTANTS.CHAT_ACTION, action);
      }
      if (from.fullPath == LOGIN_PATH) {
        this.showWindow = false;
        this.messages = [];
        this.threadId = "";
        this.assistantId = "";
      }
      next();
    });
  }
  checkCompatibility() {
    if (!this.recognition) {
      this.error = CHAT_BOT_CONSTANTS.COMPATIBILITY_ERROR;
    }
  }

  async handleInputUser() {
    this.disableInput = true;

    const content: ChatMessages = {
      role: "user",
      content: this.inputUser
    };
    this.messages.push(content);

    this.loading = true;
    const response = await this.chatService.sendChatProp(
      this.inputUser,
      !this.threadId || this.threadId == "" ? undefined : this.threadId,
      !this.assistantId || this.assistantId == "" ? undefined : this.assistantId
    );

    if (response.code == Http.Ok) {
      this.loading = false;
      if (response.data?.extraparams) {
        this.handleExtraParams(
          response.data.extraparams.key,
          response.data.extraparams.value
        );
      }
      if (this.threadId == "" && response.data?.threadId) {
        this.threadId = response.data?.threadId;
      }
      this.assistantId = response.data?.assistantId ?? "";
      this.messages.push({
        role: "assistant",
        content: response.message
      });
      this.disableInput = false;
      this.inputUser = "";
    } else {
      Snackbar.popError(CHAT_ERRORS.ERROR_RESPONSE);
    }
  }
  closeChatWindow() {
    this.showWindow = false;
    this.endSpeechRecognition();
    const action: ChatComunication = {
      action: this.ACTIONS.CLOSE_CHAT
    };

    store.commit(STORE_CONSTANTS.CHAT_ACTION, action);
  }

  @Watch("chatAction")
  handleChatAction() {
    const action = this.chatAction.action;
    if (action == "" || action == this.ACTIONS.CLOSE_CHAT) {
      return;
    }

    switch (action) {
      case this.ACTIONS.OPEN_CHAT: {
        this.showWindow = true;
        break;
      }
      case this.ACTIONS.START_SPEECH_RECORD: {
        this.startSpeechRecognition();
        break;
      }
      case this.ACTIONS.STOP_SPEECH_RECORD: {
        this.endSpeechRecognition();
        break;
      }
    }
    this.disengageAction();
  }

  disengageAction() {
    store.commit(STORE_CONSTANTS.CHAT_ACTION, {
      action: ""
    });
  }

  startSpeechRecognition() {
    if (!this.recognition) {
      return false;
    }
    this.recognition.lang = CHAT_BOT_CONSTANTS.RECOGNITION_LANG;
    this.recognition.interimResults = true;

    this.recognition.addEventListener("result", event => {
      const text = Array.from(event.results)
        .map(result => result[0])
        .map(result => result.transcript)
        .join("");
      this.runtimeTranscription = text;
    });

    this.recognition.addEventListener("end", () => {
      if (this.runtimeTranscription !== "") {
        this.setText(this.capitalizeFirstLetter(this.runtimeTranscription));
      }

      this.runtimeTranscription = "";
      this.recognition.stop();
    });

    this.recognition.start();
  }

  endSpeechRecognition() {
    this.recognition.stop();
  }

  capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  setText(string: string) {
    this.inputUser = string;
  }

  handleExtraParams(action: string, value: string) {
    if (action == "" || value == "") {
      Snackbar.popError(CHAT_ERRORS.ACTION_ERROR);
      return;
    }

    switch (action) {
      case CHAT_ACTIONS_RESPONSES.NAVEGATING_TO:
        this.$router.push(value);
        break;
      default:
        Snackbar.popError(CHAT_ERRORS.ACTION_ERROR);
        break;
    }
  }
}
